import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { useLogin } from "@/hooks/mutations/auth";
import { Toaster } from 'sonner';
import logo from "@/assets/images/logo.png";
import { motion } from "framer-motion";
import { authService } from "@/services/authService";

export function Login() {
  const navigate = useNavigate();
  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginMutation = useLogin({
    onSuccess: ({ data }) => {
      if (!data) {
        throw new Error("Invalid response data");
      }

      // Save the entire login response to localStorage
      localStorage.setItem('loginResponse', JSON.stringify(data));

      // Check if user role is "User" and has no form submission
      if (data.user.roles[0].title === "User" && data.form_submission === 0) {
        toast.success("Login successful!");
        navigate("/video");
        return;
      }

      toast.success("Login successful!");
      navigate("/");
    },
    onError: (error) => {
      // console.log('Login Error:', error); // Debugging: Log the error object
      if (error?.response?.status === 422) {
        // console.log('Invalid input');
        
        toast.custom((t) => (
          <div className="bg-[#F16745] text-white text-base px-6 py-4 rounded-lg shadow-md flex items-center gap-3 whitespace-nowrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Invalid email or password. Please try again.
          </div>
        ), {
          duration: 3000,
          position: "top-right",
          style: { zIndex: 9999 }
        });        
        return;
      }
    
      if (error?.response?.status === 401) {
        // console.log('Authentication Error');
        toast.error("", {
          description: "Invalid email or password. Please try again.",
          duration: 3000,
          position: "top-right",
        });
        return;
      }
    
      // Default error message
      toast.error("Login Failed", {
        description: error.message || "Something went wrong. Please try again.",
        duration: 3000,
        position: "top-right",
      });
    },
  });

  const handleOtpRequest = async (email) => {
    setIsLoading(true);
    try {
      await authService.loginWithOtp({ email });
      toast.custom((t) => (
        <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          OTP sent successfully!
        </div>
      ), {
        duration: 2000,
        position: "top-right",
        style: {
          zIndex: 9999,
        }
      });
      
      setTimeout(() => {
        navigate("/otp-verification", { 
          state: { email }
        });
      }, 1000);
      
    } catch (error) {
      console.error('OTP Send Error:', error);
      
      // Handle validation errors (422)
      if (error?.response?.status === 422) {
        const errorMessage = error.response?.data?.message || 
                           (error.response?.data?.errors?.email && error.response?.data?.errors?.email[0]) ||
                           "Invalid email address";
        
        toast.custom((t) => (
          <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            {errorMessage}
          </div>
        ), {
          position: "top-right",
          style: {
            zIndex: 9999,
          }
        });
      } else {
        // Handle other errors
        toast.custom((t) => (
          <div className="bg-[#F16745] text-white px-6 py-4 rounded-lg shadow-md flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            {error.response?.data?.message || "Failed to send OTP"}
          </div>
        ), {
          position: "top-right",
          style: {
            zIndex: 9999,
          }
        });
      }
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: !isOtpLogin ? Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required") : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isOtpLogin) {
        handleOtpRequest(values.email);
      } else {
        loginMutation.mutate({
          email: values.email,
          password: values.password,
        });
      }
    },
  });

  return (
    <>
      <Toaster
        richColors
        position="top-right"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
          duration: 3000,
        }}
      />
      <div className="min-h-screen flex items-center justify-center flex-col" style={{ backgroundColor: '#F1EBDB' }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg"
        >
          <div>
            {/* <div className="flex justify-center mb-6">
              <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
            </div> */}
            <h2 className="text-3xl font-bold text-center text-black">Welcome to PostScrypt</h2>
            <p className="mt-2 text-sm text-[#F16745] text-center mb-8">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="rounded-md">
              <div className="mb-4">
                <label htmlFor="email" className="block font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:text-black"
                  placeholder="Enter your email"
                />
                {formik.submitCount > 0 && formik.errors.email && (
                  <p className="mt-2 text-red-600">{formik.errors.email}</p>
                )}
              </div>

              {/* Conditionally render password field */}
              {!isOtpLogin && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4"
                >
                  <div className="mb-4">
                    <label htmlFor="password" className="block font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      name="password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:text-black"
                      placeholder="Enter your password"
                    />
                    {formik.submitCount > 0 && formik.errors.password && (
                      <p className="mt-2 text-red-600">{formik.errors.password}</p>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div></div> {/* Empty div to maintain spacing */}
              
              {/* Only show Forgot Password link when not in OTP login mode */}
              {!isOtpLogin && (
                <motion.button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-[#F16745] hover:text-[#F16745]/90 focus:outline-none"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Forgot Password?
                </motion.button>
              )}
            </div>

            <motion.button
              type="submit"
              disabled={isLoading || loginMutation.isPending}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 hover:scale-[1.02]"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {(isLoading || loginMutation.isPending) ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isOtpLogin ? "Sending OTP..." : "Signing in..."}
                </span>
              ) : (
                isOtpLogin ? "Send OTP" : "Sign in"
              )}
            </motion.button>
            
            {/* OTP login button with matching styles to main button */}
            <motion.button
              type="button"
              onClick={() => setIsOtpLogin(!isOtpLogin)}
              className="mt-4 w-full flex justify-center py-2 px-4 border border-[#F16745] text-sm font-medium rounded-md text-[#F16745] hover:bg-[#F1EBDB]/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] transform transition-all duration-150"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isOtpLogin ? "Login with Password" : "Login with OTP"}
            </motion.button>
          </form>
        </motion.div>
        <div className="mt-8 mb-4">
          <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
        </div>
      </div>
    </>
  );
}
